/* verqo — Buyer Hero V2 Preview
   Isolated preview of the proposed buyer hero redesign:
     · NEW eyebrow target line ("For retailers, resellers & boutique brands · $250K–$25M")
     · NEW 1-to-Many "Portable credit" graphic — inverse of seller's "Six players. One ledger."
     · Existing hero copy (already shipped on /): "Stop paying cash upfront. Buy on terms. Everywhere."
   This route is preview-only. If approved, will be merged into LandingV2Preview.jsx.
*/
import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  ArrowRight, BadgePercent, CircleDot, FileSignature, Sparkles,
  CheckCircle2, Shirt, Heart, UtensilsCrossed, Home, Smartphone, PawPrint, Gem, ShoppingBasket,
} from "lucide-react";
import { VerqoLockup } from "../../components/VerqoLockup";

/* ────────────────────────────────────────────────────────────────────── */
/* BuyerCreditGraph — "1 credit line → many suppliers"                    */
/* Inverse of NetworkGraph (which is "6 parties → 1 ledger")              */
/* Exported so LandingV2Preview can reuse it when buyerHeroV2 is enabled. */
/* ────────────────────────────────────────────────────────────────────── */
export function BuyerCreditGraph() {
  // 8 supplier categories represented as orbiting nodes.
  // Angles offset 22.5° from cardinal directions so no node sits at 0°/90°/180°/270°
  // (cleaner layout — leaves room for center labels + caption breathing room).
  const suppliers = [
    { id: "fashion",   icon: Shirt,           angle: -112.5 },
    { id: "beauty",    icon: Sparkles,        angle:  -67.5 },
    { id: "food",      icon: UtensilsCrossed, angle:  -22.5 },
    { id: "wellness",  icon: Heart,           angle:   22.5 },
    { id: "luxury",    icon: Gem,             angle:   67.5 },
    { id: "home",      icon: Home,            angle:  112.5 },
    { id: "tech",      icon: Smartphone,      angle:  157.5 },
    { id: "pet",       icon: PawPrint,        angle: -157.5 },
  ];
  const cx = 200, cy = 200, r = 140;
  const polar = (a) => ({ x: cx + r * Math.cos(a * Math.PI / 180), y: cy + r * Math.sin(a * Math.PI / 180) });

  return (
    <div className="relative w-full max-w-[420px] aspect-square mx-auto" data-testid="buyer-credit-graph">
      <svg viewBox="0 0 400 400" className="w-full h-full overflow-visible">
        <defs>
          <radialGradient id="creditHubGlow">
            <stop offset="0%"  stopColor="#dcff3a" stopOpacity="0.42" />
            <stop offset="60%" stopColor="#dcff3a" stopOpacity="0.12" />
            <stop offset="100%" stopColor="#dcff3a" stopOpacity="0" />
          </radialGradient>
        </defs>

        {/* Pulsing halo around hub */}
        <motion.circle
          cx={cx} cy={cy}
          fill="url(#creditHubGlow)"
          initial={{ r: 55 }}
          animate={{ r: [55, 95, 55] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
        />

        {/* Connection lines — buyer center → each supplier (one-way reach) */}
        {suppliers.map((s) => {
          const { x, y } = polar(s.angle);
          return (
            <line
              key={`line-${s.id}`}
              x1={cx} y1={cy} x2={x} y2={y}
              stroke="rgba(220,255,58,0.22)"
              strokeWidth="1.2"
              strokeDasharray="2 3"
            />
          );
        })}

        {/* Pulses radiating OUTWARD only (credit reaches out, no return flow) */}
        {suppliers.map((s, i) => {
          const { x, y } = polar(s.angle);
          return (
            <motion.circle
              key={`pulse-${s.id}`}
              r="3"
              fill="#dcff3a"
              initial={{ cx: cx, cy: cy, opacity: 0 }}
              animate={{ cx: [cx, x], cy: [cy, y], opacity: [0, 1, 1, 0] }}
              transition={{ duration: 2.2, repeat: Infinity, delay: i * 0.26, ease: "easeOut" }}
            />
          );
        })}

        {/* Center hub — V glyph in lime circle */}
        <circle cx={cx} cy={cy} r="44" fill="#dcff3a" />
        <path
          d={`M ${cx - 24} ${cy - 12} L ${cx - 4} ${cy + 18} L ${cx + 24} ${cy - 26}`}
          stroke="#0c1612"
          strokeWidth="8"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        {/* $250K label inside the hub, directly under the V check */}
        <text
          x={cx} y={cy + 32}
          textAnchor="middle" fontSize="11" fontWeight="800" letterSpacing="0.5"
          fill="#0c1612" fontFamily="Inter, system-ui, sans-serif"
        >
          $250K
        </text>
      </svg>

      {/* HTML labels positioned over SVG — one per supplier orbit node */}
      {suppliers.map((s, i) => {
        const { x, y } = polar(s.angle);
        const xPct = (x / 400) * 100;
        const yPct = (y / 400) * 100;
        return (
          <motion.div
            key={s.id}
            className="absolute"
            style={{ left: `${xPct}%`, top: `${yPct}%`, transform: "translate(-50%, -50%)" }}
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 + i * 0.09, ease: "easeOut" }}
            data-testid={`buyer-supplier-node-${s.id}`}
          >
            <div className="relative">
              <div className="w-11 h-11 rounded-full bg-white border-2 border-lime-400/80 grid place-items-center shadow-[0_0_16px_-4px_rgba(220,255,58,0.55)]">
                <s.icon className="w-4 h-4 text-slate-900" strokeWidth={1.8} />
              </div>
              <motion.div
                className="absolute -top-1 -right-1 w-4.5 h-4.5 rounded-full bg-lime-400 border-2 border-slate-900 grid place-items-center"
                style={{ width: "18px", height: "18px" }}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.35, delay: 1.0 + i * 0.1, ease: "backOut" }}
              >
                <CheckCircle2 className="w-2.5 h-2.5 text-slate-900" strokeWidth={3} />
              </motion.div>
            </div>
          </motion.div>
        );
      })}

      {/* PRE-APPROVED caption — positioned just below the V hub circle, inside SVG bounds */}
      <div className="absolute left-1/2 -translate-x-1/2 text-center" style={{ top: "calc(50% + 64px)" }}>
        <div className="text-[9px] uppercase tracking-[0.3em] text-lime-400/80 font-semibold whitespace-nowrap">
          pre-approved
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Eyebrow + Reveal helpers (lightweight copies for isolated preview)     */
/* ────────────────────────────────────────────────────────────────────── */
function Eyebrow({ children, testid }) {
  return (
    <div
      className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em] text-lime-700 font-semibold"
      data-testid={testid}
    >
      <CircleDot className="w-2.5 h-2.5 fill-lime-500 text-lime-500" />
      {children}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────── */
/* Buyer Hero V2 — proposed redesign · isolated preview                   */
/* ────────────────────────────────────────────────────────────────────── */
export default function BuyerHeroV2Preview() {
  useEffect(() => {
    document.title = "verqo · Buyer Hero V2 Preview";
  }, []);

  return (
    <div className="min-h-screen bg-[#FAFAF7] text-slate-900 antialiased selection:bg-lime-300" data-testid="buyer-hero-v2-preview">
      {/* Floating preview-mode pill */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-50 border border-amber-300 text-[11px] uppercase tracking-[0.2em] text-amber-800 shadow-lg" data-testid="preview-banner">
        <CircleDot className="w-2.5 h-2.5 fill-amber-500 text-amber-500" />
        PREVIEW · Buyer Hero V2
      </div>

      {/* Minimal nav so it feels like a real page */}
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-md bg-white/80 border-b border-slate-200/60">
        <div className="max-w-[1280px] mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <a href="/" className="inline-flex items-center" data-testid="preview-back-home">
            <VerqoLockup />
            <span className="ml-1 text-[11px] text-slate-500 hidden md:inline">· Wholesale, instant.</span>
          </a>
          <div className="flex items-center gap-4">
            <a href="/" data-testid="preview-back-live" className="text-[12px] text-slate-600 hover:text-slate-900 transition-colors">
              ← Back to live homepage
            </a>
          </div>
        </div>
      </header>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* BUYER HERO V2 — the proposed redesign                           */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <section id="for-buyers" className="relative pt-32 md:pt-40 pb-20 md:pb-24 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="section-for-buyers-v2">
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-16 items-center">
          {/* LEFT — copy + CTAs */}
          <div>
            <Eyebrow testid="buyer-hero-v2-eyebrow">For Retailers, independent shops &amp; Online Resellers · $250K–$100M</Eyebrow>
            <h2 className="mt-5 text-[40px] md:text-[60px] lg:text-[72px] leading-[0.96] tracking-tight font-semibold text-slate-900">
              Stop paying <span className="bg-lime-300 px-2 -rotate-[0.4deg] inline-block">cash upfront.</span><br />
              <span className="text-slate-400">Buy on terms. Everywhere.</span>
            </h2>
            <p className="mt-7 max-w-xl text-[16px] md:text-[18px] leading-relaxed text-slate-600">
              Apply once. Get pre-approved for <strong className="text-slate-900">Net-30/60/90</strong> in 60 seconds. Use it at every supplier on verqo — or <strong className="text-slate-900">bring your existing suppliers on with one PO.</strong>
            </p>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <a
                href="/register?as=buyer"
                data-testid="buyer-hero-v2-cta-primary"
                className="group inline-flex items-center gap-2 px-6 py-3.5 rounded-lg text-white text-sm font-semibold hover:opacity-90 transition-all"
                style={{ background: "#0c1612" }}
              >
                Get pre-approved — free
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a href="/login" data-testid="buyer-hero-v2-signin" className="text-[13.5px] text-slate-600 hover:text-slate-900 transition-colors">
                Already on verqo? Sign in →
              </a>
            </div>
            <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3 text-[12px] text-slate-500">
              <div className="inline-flex items-center gap-1.5" data-testid="buyer-hero-v2-promise-1">
                <CheckCircle2 className="w-3.5 h-3.5 text-lime-600" /> Approved in 60 seconds
              </div>
              <div className="inline-flex items-center gap-1.5" data-testid="buyer-hero-v2-promise-2">
                <CheckCircle2 className="w-3.5 h-3.5 text-lime-600" /> Pay $0 to verqo — always
              </div>
            </div>
          </div>

          {/* RIGHT — 1-to-Many credit graphic in dark "product preview" card */}
          <div className="relative rounded-2xl p-6 md:p-7 overflow-hidden shadow-[0_40px_80px_-32px_rgba(15,23,42,0.45)]" style={{ background: "#0c1612" }} data-testid="buyer-hero-v2-graphic-card">
            {/* Grid background */}
            <div className="absolute inset-0 opacity-[0.05] pointer-events-none"
              style={{ backgroundImage: "linear-gradient(rgba(220,255,58,1) 1px, transparent 1px), linear-gradient(90deg, rgba(220,255,58,1) 1px, transparent 1px)", backgroundSize: "28px 28px" }}
            />
            {/* Radial glow */}
            <div
              className="absolute inset-0 opacity-30 pointer-events-none"
              style={{ background: "radial-gradient(circle at 50% 40%, rgba(220,255,58,0.18) 0%, transparent 55%)" }}
            />
            <div className="relative">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[10px] uppercase tracking-[0.26em] font-semibold text-lime-400">Your portable credit line</div>
                <div className="inline-flex items-center gap-1.5 text-[10px] text-white/55 uppercase tracking-wider">
                  <span className="w-1.5 h-1.5 rounded-full bg-lime-300 animate-pulse" /> Live
                </div>
              </div>
              <BuyerCreditGraph />
              <div className="mt-3 text-center">
                <div className="text-[19px] md:text-[21px] font-semibold text-white tracking-tight leading-tight">
                  One line. <span className="text-white/45">Every supplier.</span>
                </div>
                <div className="text-[11.5px] text-white/55 mt-1.5 max-w-[320px] mx-auto leading-relaxed">
                  Approved once · honored at every brand on verqo · use the proof to negotiate harder off-network.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Side-by-side comparison helper — "before vs after" reference */}
      <section className="relative pt-4 pb-32 px-6 md:px-12 max-w-[1280px] mx-auto" data-testid="comparison-helper">
        <div className="rounded-2xl border border-slate-200 bg-white p-7 md:p-9" data-testid="comparison-card">
          <Eyebrow>What changed from live</Eyebrow>
          <h3 className="mt-4 text-xl md:text-2xl font-semibold tracking-tight text-slate-900 leading-snug">
            Two upgrades. Same hero copy.
          </h3>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-5">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-lime-700 font-semibold mb-2">Upgrade 1 · Target eyebrow</div>
              <p className="text-[13.5px] text-slate-700 leading-relaxed">
                Added <strong>"For retailers, resellers &amp; boutique brands · $250K–$25M"</strong> — mirrors the seller eyebrow exactly, sets a revenue floor, and tells the visitor "this is for me" in one second.
              </p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50/40 p-5">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-lime-700 font-semibold mb-2">Upgrade 2 · Right-side visual</div>
              <p className="text-[13.5px] text-slate-700 leading-relaxed">
                Replaced the empty space (where 3 bullet cards used to live) with a <strong>1-to-Many "portable credit" graphic</strong> — the inverse of the seller's six-players-one-ledger. Central V + $250K pre-approved badge, with 8 supplier categories pulsing in green ✓ "approved" badges around it.
              </p>
            </div>
          </div>
          <div className="mt-7 rounded-xl border border-lime-300 bg-lime-50/50 p-5">
            <div className="text-[13.5px] text-slate-900 leading-relaxed">
              ⚠️ <strong>What's preserved:</strong> The 3 proof bullets (Get pre-approved first · Bring your suppliers · Pay $0) and the "Two ways to win" section already live on <code className="bg-white px-1.5 py-0.5 rounded text-[12px]">/</code> would move <strong>below</strong> this hero in the live merge — exactly like the seller side has bento tiles below its hero.
            </div>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3">
            <a href="/" data-testid="back-to-live" className="inline-flex items-center gap-2 px-5 py-3 rounded-lg border border-slate-300 text-slate-900 text-[13px] font-semibold hover:bg-slate-50 transition-colors">
              ← Compare with live homepage
            </a>
            <div className="text-[12px] text-slate-500">
              Approve in chat to merge into <code className="bg-slate-100 px-1.5 py-0.5 rounded">/</code>.
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
